/**
 * Build-tab–style prompt shell: optional alert band + top deck (user content) + bottom deck (left/right controls).
 * Use one instance so Agentic and Network Workbench stay visually in sync.
 *
 * With ``alertBand``: Claude-style stack — the band is a separate surface **behind** (`z-0`) the prompt
 * card (`z-10`), joined by ``-mb-1`` so the prompt visually sits on top rather than one merged box.
 */
import React from 'react';
import { WORKBENCH_PROMPT_INNER_WRAPPER, WORKBENCH_PROMPT_ALERT_BAND_OUTER_PADDING } from '../utils/workbenchPromptFieldClassNames.js';

/** Prompt card chrome when stacked above a rear alert band */
const PROMPT_CARD_STACKED =
  [
    'relative z-10 overflow-hidden rounded-[20px] bg-white',
    'border border-gray-200 shadow-[0_0.25rem_1.25rem_rgba(15,23,42,0.07),0_0_0_0.5px_rgba(148,163,184,0.2)]',
    'transition-[border-color,box-shadow] duration-200 ease-out',
    'hover:border-gray-300 hover:shadow-[0_0.35rem_1.35rem_rgba(15,23,42,0.09),0_0_0_0.5px_rgba(148,163,184,0.28)]',
    'focus-within:border-blue-400/80 focus-within:shadow-[0_0.35rem_1.35rem_rgba(15,23,42,0.1),0_0_0_0.5px_rgba(96,165,250,0.35)]',
    'focus-within:ring-1 focus-within:ring-blue-400/20',
  ].join(' ');

/** Single-card layout when there is no alert band */
const PROMPT_CARD_SOLO =
  [
    'overflow-hidden rounded-3xl border border-gray-200 bg-white',
    'transition-[border-color] duration-150 ease-out',
    'hover:border-gray-300',
    'focus-within:border-blue-400/80 focus-within:ring-1 focus-within:ring-blue-400/25',
  ].join(' ');

function PromptCardBody({ top, bottomLeft, bottomRight, singleRow }) {
  // ONE stable DOM structure for both layouts — only CSS changes between
  // single-row and two-deck. Switching trees (fragment vs div) would unmount/
  // remount ``top``; for the Build tab's CONTENTEDITABLE that recreates the node
  // and DROPS the typed text on transition (the room <textarea> is value-
  // controlled so it survived, but contenteditable is DOM-state). Keeping the
  // same wrapper + only flipping classes preserves the node, hence the text.
  //
  //  - single-row: outer is a flex ROW → [+]  input(grow)  [send]
  //  - two-deck:   outer is a flex COL → input row on top, controls row below
  return (
    <div className={singleRow
      ? 'flex min-h-0 flex-row items-center gap-2 py-1 pl-1 pr-1'
      : 'flex min-h-0 flex-col'}>
      {/* Input — order-1 always; full width in two-deck (p-4), flex-1 in row. */}
      <div className={singleRow
        ? `order-2 min-w-0 flex-1 ${WORKBENCH_PROMPT_INNER_WRAPPER}`
        : `order-1 p-4 ${WORKBENCH_PROMPT_INNER_WRAPPER}`}>
        {top}
      </div>
      {/* Controls row. In single-row they split (+ left of input, send right) via
          order; in two-deck they sit together on the bottom deck with a divider. */}
      {singleRow ? (
        <>
          {bottomLeft ? <div className="order-1 ml-0.5 flex shrink-0 items-center">{bottomLeft}</div> : null}
          {bottomRight ? <div className="order-3 mr-0.5 flex shrink-0 items-center">{bottomRight}</div> : null}
        </>
      ) : (
        <div className="order-2 flex min-h-0 items-center justify-between border-t border-gray-100 px-4 py-1.5 sm:px-5">
          <div className="flex items-center">{bottomLeft}</div>
          <div className="flex shrink-0 items-center justify-end">{bottomRight}</div>
        </div>
      )}
    </div>
  );
}

/**
 * @param {object} props
 * @param {React.ReactNode} props.top — prompt input (e.g. contenteditable or textarea)
 * @param {React.ReactNode} [props.bottomLeft] — second-deck left (e.g. Build approval pill)
 * @param {React.ReactNode} [props.bottomRight] — second-deck right (e.g. send / Run)
 * @param {string} [props.className] — extra classes on the prompt card (stacked or solo)
 * @param {import('react').ReactNode} [props.hint] — small hint under the card
 * @param {import('react').ReactNode} [props.alertBand] — rear notice card; prompt card sits in front (Claude-style).
 */
export default function WorkbenchPromptCard({
  top,
  bottomLeft = null,
  bottomRight = null,
  className = '',
  hint = null,
  alertBand = null,
  singleRow = false,
}) {
  const hasBand = Boolean(alertBand);
  // One-liner shell is a full pill (rounded-full) so the box curve matches the
  // rounded-full + / send buttons sitting inside it.
  const soloShell = singleRow
    ? PROMPT_CARD_SOLO.replace('rounded-3xl', 'rounded-full')
    : PROMPT_CARD_SOLO;

  if (!hasBand) {
    return (
      <div>
        <div className={`${soloShell} ${className}`.trim()}>
          <PromptCardBody top={top} bottomLeft={bottomLeft} bottomRight={bottomRight} singleRow={singleRow} />
        </div>
        {hint ? (
          <div className="mt-2 text-center text-xs text-gray-500">{hint}</div>
        ) : null}
      </div>
    );
  }

  return (
    <div className="isolate w-full min-w-0">
      {/*
        Rear layer: not part of the white prompt box — separate card that the prompt overlaps.
        Mirrors Claude: px-3 md:px-2 wrapper, z-0, rounded-t-xl, -mb-1, border-b-0, muted surface.
      */}
      <div className={WORKBENCH_PROMPT_ALERT_BAND_OUTER_PADDING} data-prompt-alert-band-wrapper="true">
        <div role="status" aria-live="polite" aria-atomic="true">
          <div className="relative z-0 w-full -mb-1 rounded-t-xl border-[0.5px] border-transparent border-b-0 bg-gray-200/90 px-3.5 pb-2.5 pt-2 shadow-[inset_0_1px_0_rgba(255,255,255,0.35)]">
            {alertBand}
          </div>
        </div>
      </div>

      {/* Front layer: prompt “window” — casts shadow over the band */}
      <div className="relative z-10">
        <div className={`${(singleRow ? PROMPT_CARD_STACKED.replace('rounded-[20px]', 'rounded-full') : PROMPT_CARD_STACKED)} ${className}`.trim()}>
          <PromptCardBody top={top} bottomLeft={bottomLeft} bottomRight={bottomRight} singleRow={singleRow} />
        </div>
      </div>

      {hint ? (
        <div className="mt-2 text-center text-xs text-gray-500">{hint}</div>
      ) : null}
    </div>
  );
}
